<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref } from 'vue'
import { useFormatDate } from '../composables/formatDate'
import FormHeader from './FormHeader.vue'
import TypeAheadUsers from './TypeAheadUsers.vue'
import { Message, sanitizeMessage } from '../store/useMessages'
import { useAPIURLs } from '../composables/useAPIURLs'
import { useI18n } from 'vue-i18n'

type FormProps = {
  /**Locks the subject and user */
  reply: boolean
  sent?: boolean
  message: undefined | { message: Message; sent: boolean }
}

const props = withDefaults(defineProps<FormProps>(), {
  reply: false,
})
const emit = defineEmits<{
  (e: 'send', data: any): void
}>()

const { t } = useI18n({ useScope: 'global' })
const form = (label: string) => t(`form.${label}`)
const formLabel = (label: string) => t(`form.messages.${label}`)

const user = ref()
const userRef = ref()
const subject = ref()
const subjectRef = ref()
const body = ref()
const bodyRef = ref()

onBeforeMount(() => {
  close.value = false
  if (props.reply && props.message) {
    subject.value = props.message.message.subject.startsWith('Re: ')
      ? props.message?.message.subject
      : `Re: ${props.message?.message.subject}`
    body.value = renderBody(props.message?.message)
  }
})
onMounted(() => {
  if (props.reply && props.message) {
    //focus at start of input
    const el = bodyRef.value.getNativeElement()
    el.setSelectionRange(0, 0)
  }
})
const renderDate = computed(() => {
  const messageDate = useFormatDate(props.message?.message.date)
  return `${messageDate.weekday()}, ${messageDate.month()} ${messageDate.day()}, ${messageDate.time()}`
})

// const toReg = new RegExp(`<${props.message?.message.to.username}>`)
// const fromReg = new RegExp(`<${props.message?.message.from.username}>`)

const splitLines = (data: string) => {
  const splitData = data.replaceAll('\n', '')

  return `>${
    sanitizeMessage(splitData)
      // render < > tags for usernames etc
      .replaceAll(
        `&lt;${props.message?.message.to.username}&gt;`,
        `<${props.message?.message.to.username}>`
      )
      .replaceAll(
        `&lt;${props.message?.message.from.username}&gt;`,
        `<${props.message?.message.from.username}>`
      )
    // .replaceAll('&gt;', '\n>')
    // .replaceAll('&gt;', '>')
    // .replaceAll('&lt;', '<')
    //
  }
    `
}
const renderBody = (data: Message) => {
  return `\n\n>On ${renderDate.value}, ${data.from.name} <${
    data.from.username
  }>  wrote:\n>\n${splitLines(data.body)}`
}

const close = ref(false)
const validate = () => {
  let userValidate = true
  let subjectValidate = true
  let bodyValidate = true
  if (userRef.value) {
    userValidate = userRef.value.validate()
  }
  if (subjectRef.value) {
    subjectValidate = subjectRef.value.validate()
  }
  if (bodyRef.value) {
    bodyValidate = bodyRef.value.validate()
  }
  return userValidate && subjectValidate && bodyValidate
}
const cleanMessage = (body: string) => {
  return sanitizeMessage(
    body
      // .replaceAll('>', '&gt;')
      // .replaceAll('<', '&lt;')
      .replaceAll(
        `<${props.message?.message.to.username}>`,
        `&lt;${props.message?.message.to.username}&gt;`
      )
      .replaceAll(
        `<${props.message?.message.from.username}>`,
        `&lt;${props.message?.message.from.username}&gt;`
      )
      .replaceAll(/(?:\r\n|\r|\n)/g, '<p>')
  )
}
const sendMessage = () => {
  if (validate()) {
    emit('send', {
      to: user.value,
      subject: subject.value,
      body: cleanMessage(body.value),
    })
    close.value = true
  }
}
const { API } = useAPIURLs()
</script>
<template>
  <FormHeader :header-text="formLabel('reply')" v-bind="user" />

  <q-card-section class="q-pt-none">
    <div v-show="userRef" class="column q-gutter-sm q-my-sm">
      <TypeAheadUsers
        ref="userRef"
        hide-bottom-space
        v-model="user"
        :label="form('to')"
        :route="API.getMessagingUsers"
        :autofocus="!reply"
        :readonly="reply"
        :rules='[ (val: any) => (typeof val !== "undefined" && val !== null) || form("requiredGeneric"),]'
        :username="sent ? message?.message.to : message?.message.from"
      />
      <q-input
        ref="subjectRef"
        hide-bottom-space
        :readonly="reply"
        filled
        color="accent"
        v-model="subject"
        :rules='[ (val: any) => (typeof val !== "undefined" && val !== null) || form("requiredGeneric"),]'
        type="text"
        :label="formLabel('subject')"
      />
      <q-input
        :autofocus="reply"
        data-body-input
        ref="bodyRef"
        hide-bottom-space
        filled
        color="accent"
        v-model="body"
        type="textarea"
        :rules='[ (val: any) => (typeof val !== "undefined" && val !== null) || form("requiredGeneric"),]'
        :label="formLabel('message')"
      />
    </div>
  </q-card-section>
  <q-card-actions align="right" class="text-primary">
    <q-btn no-caps flat :label="t('form.close')" v-close-popup />
    <q-btn
      no-caps
      rounded
      @click="sendMessage"
      color="primary"
      :label="form('sendMessage')"
      v-close-popup="close"
    />
  </q-card-actions>
</template>
